'use client';

import { useMemo } from 'react';
import { useScheduler } from '../SchedulerContext';
import { useTZDayjs } from '@/context/TimezoneContext';
import { Avatar, Tooltip } from '@heroui/react';
import {
  FaScrewdriverWrench,
  FaHouseChimney,
  FaCalendarDay,
  FaUser,
  FaMagnifyingGlass,
  FaRegNoteSticky,
} from 'react-icons/fa6';
import ConfidenceChip from '@/components/ui/ConfidenceChip';
import { Bundle, ContactRoleType, ServWithMods } from '@/util/types/serverTypes';
import * as Icons from 'react-icons/fa6';
import EditableInvoiceTable from '@/components/ui/EditableInvoiceTable';
import { useCountdown } from '../hooks/useCountdown';
import BookingJobLoader from './BookingJobLoader';
import SendQuoteLoader from './SendQuoteLoader';
import DiscountCodeInput from '@/components/scheduling/DiscountCodeInput';
import { formatPhoneForDisplay } from '@/util/functions/formatPhoneForDisplay';

type SummaryStepProps = {
  services: ServWithMods[];
  bundles: Bundle[];
  contactRoles: ContactRoleType[];
};

// Helper to get icon component
const getIconComponent = (iconName: string) => {
  const IconComponent = (Icons as any)[iconName];
  return IconComponent ? <IconComponent size={20} /> : null;
};

export default function SummaryStep({ services, bundles, contactRoles }: SummaryStepProps) {
  const {
    selectedPrimaryService,
    selectedBundle,
    selectedAddons,
    address,
    streetViewUrl,
    propertyDetails,
    selectedDateTime,
    contact,
    additionalContacts,
    selectedSlotInspectorInfo,
    selectedRole,
    setSelectedPrimaryService,
    setSelectedAddons,
    setSelectedBundle,
    finalServices,
    priceData,
    scheduleSettings,
    workingHoldExpiresAt,
    bookingStatus,
    setBookingStatus,
    isSendingQuote,
    setIsSendingQuote,
    companyStyles,
    company,
    discountCodes,
    appliedDiscountCode,
    appliedDiscountServiceIds,
    setAppliedDiscountCode,
    setAppliedDiscountServiceIds,
    selectedServiceIds,
    aiRecommendations,
    schedulerNotes,
    setSchedulerNotes,
  } = useScheduler();

  const tzDayjs = useTZDayjs();

  // Countdown timer for working hold
  const countdown = useCountdown(workingHoldExpiresAt);

  // Handle remove action
  const handleRemove = ({
    serviceId,
    bundleId,
    isBundle,
    removeServiceFromBundle,
  }: {
    serviceId?: string;
    bundleId?: string;
    isBundle?: boolean;
    removeServiceFromBundle?: boolean;
  }) => {
    // Prevent removing the primary service
    if (serviceId === selectedPrimaryService) {
      return;
    }

    if (removeServiceFromBundle && serviceId && bundleId) {
      // Remove a single service from within a bundle
      // This disbands the bundle but keeps other services at normal price
      const bundle = bundles.find((b) => b._id === bundleId);

      if (bundle) {
        // Get all service IDs from the bundle except the one being removed
        const remainingBundleServiceIds = bundle.items
          .map((item) => item._serviceId)
          .filter((id) => id !== serviceId && id !== selectedPrimaryService);

        // Add remaining bundle services to addons (so they stay selected without bundle discount)
        setSelectedAddons((prev) => {
          const combined = new Set([...prev, ...remainingBundleServiceIds]);
          // Remove the service being deleted
          combined.delete(serviceId);
          return Array.from(combined);
        });
      }

      // Clear the bundle
      setSelectedBundle(null);

      // Remove the specific service if it's the primary
      if (serviceId === selectedPrimaryService) {
        setSelectedPrimaryService(null);
      }
    } else if (isBundle && bundleId) {
      // Remove bundle (only one bundle can be selected)
      if (selectedBundle === bundleId) {
        setSelectedBundle(null);
      }

      // Also remove any services from this bundle that were added as addons
      const bundle = bundles.find((b) => b._id === bundleId);
      if (bundle) {
        const bundleServiceIds = bundle.items.map((item) => item._serviceId);
        setSelectedAddons((prev) => prev.filter((addonId) => !bundleServiceIds.includes(addonId)));

        // If primary service is part of the bundle, remove it too
        if (bundle.items.some((item) => item._serviceId === selectedPrimaryService)) {
          setSelectedPrimaryService(null);
        }
      }
    } else if (serviceId) {
      // Remove individual service
      // Check if it's the primary service
      if (serviceId === selectedPrimaryService) {
        setSelectedPrimaryService(null);
      }
      // Check if it's an addon
      if (selectedAddons.includes(serviceId)) {
        setSelectedAddons((prev) => prev.filter((id) => id !== serviceId));
      }
    }
  };

  // Calculate property age
  const propertyAge = useMemo(() => {
    const year = address?.yearBuilt;
    return year ? new Date().getFullYear() - year : null;
  }, [address]);

  // Get appointment duration from context (in hours, need to convert to minutes for time calculation)
  const appointmentDuration = (priceData?.totalDuration || 0) * 60;

  // Calculate time range
  const timeRange = useMemo(() => {
    if (!selectedDateTime || !appointmentDuration) return null;
    const startTime = tzDayjs(selectedDateTime);
    const endTime = startTime.add(appointmentDuration, 'minutes');
    return {
      start: startTime.format('h:mma'),
      end: endTime.format('h:mma'),
    };
  }, [selectedDateTime, appointmentDuration, tzDayjs]);

  // Format duration range (e.g., '~3-4 hour duration')
  const durationRange = useMemo(() => {
    const durationInHours = priceData?.totalDuration || 0;
    if (durationInHours === 0) return null;

    const lower = Math.floor(durationInHours);
    const upper = lower + 1;

    return `~${lower}-${upper} hours`;
  }, [priceData?.totalDuration]);

  // Filter AI recommendations to only show unadded services
  const unaddedRecommendations = useMemo(() => {
    if (!aiRecommendations?.recommendations) return [];
    return aiRecommendations.recommendations.filter(
      (rec) => !selectedServiceIds.includes(rec.serviceId),
    );
  }, [aiRecommendations, selectedServiceIds]);

  // Detect partial bundle opportunities
  const partialBundleOpportunities = useMemo(() => {
    if (!bundles || bundles.length === 0) return [];

    return bundles
      .filter((bundle) => {
        // Skip if this bundle is already applied
        if (selectedBundle === bundle._id) return false;
        // Skip inactive bundles
        if (!bundle.active) return false;

        const bundleServiceIds = bundle.items.map((item) => item._serviceId);
        const selectedFromBundle = bundleServiceIds.filter((id) => selectedServiceIds.includes(id));

        // Has SOME but NOT ALL services from the bundle
        return selectedFromBundle.length > 0 && selectedFromBundle.length < bundleServiceIds.length;
      })
      .map((bundle) => {
        const bundleServiceIds = bundle.items.map((item) => item._serviceId);
        const missingServiceIds = bundleServiceIds.filter((id) => !selectedServiceIds.includes(id));
        const missingServices = missingServiceIds
          .map((id) => services.find((s) => s._id === id))
          .filter(Boolean);

        return {
          bundle,
          missingServiceIds,
          missingServiceNames: missingServices.map((s) => s?.name || ''),
          missingCount: missingServiceIds.length,
        };
      });
  }, [bundles, selectedBundle, selectedServiceIds, services]);

  // Handlers for adding recommendations
  const handleAddRecommendedService = (serviceId: string) => {
    setSelectedAddons((prev) => [...prev, serviceId]);
  };

  const handleCompleteBundl = (bundleId: string, missingServiceIds: string[]) => {
    // Add missing services to addons
    setSelectedAddons((prev) => {
      const combined = new Set([...prev, ...missingServiceIds]);
      return Array.from(combined);
    });
    // Set the bundle as selected
    setSelectedBundle(bundleId);
  };

  // If sending quote is in progress, show the loader
  if (isSendingQuote) {
    return (
      <SendQuoteLoader
        onComplete={() => {}}
        onError={(error) => {
          setIsSendingQuote(false); // Return to summary on error
        }}
        onRetry={() => {
          setIsSendingQuote(false); // Allow user to return to summary
        }}
      />
    );
  }

  // If booking is in progress or complete, show the loader
  if (bookingStatus !== 'idle') {
    return (
      <BookingJobLoader
        onComplete={() => {
          // Keep success screen visible (bookingStatus stays 'success')
        }}
        onError={() => {
          setBookingStatus('idle'); // Return to summary on error
        }}
        onRetry={() => {
          setBookingStatus('idle'); // Allow user to return to summary
        }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold">Review Your Request</h2>
        <p className="mt-2 text-gray-600">Please review all details before submitting</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Services Section - Left Side */}
        <div className="rounded-lg border border-gray-300 bg-white p-6 lg:col-span-2">
          <div className="mb-4 flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{
                backgroundColor: companyStyles.companyColor
                  ? `${companyStyles.companyColor}1A`
                  : 'rgba(0, 111, 238, 0.1)',
                color: companyStyles.companyColor || '#006FEE',
              }}
            >
              <FaScrewdriverWrench className="text-xl" />
            </div>
            <h3 className="text-xl font-semibold">Services</h3>
          </div>

          {finalServices.length > 0 ? (
            <>
              <EditableInvoiceTable
                services={finalServices}
                onRemove={handleRemove}
                showServiceDescription={true}
                nonRemovableServiceIds={selectedPrimaryService ? [selectedPrimaryService] : []}
                usePrimaryFeeForBundleDiscount={
                  scheduleSettings?.bundleDiscountDisplayPrimaryFee ?? true
                }
              />

              {/* Need Anything Else? - Recommendations */}
              {(unaddedRecommendations.length > 0 || partialBundleOpportunities.length > 0) && (
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <div className="mb-3 flex items-center gap-2">
                    <FaMagnifyingGlass
                      className="text-lg"
                      style={{ color: companyStyles.companyColor || '#006FEE' }}
                    />
                    <h4 className="text-lg font-semibold">Need Anything Else?</h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {/* Partial Bundle Opportunities */}
                    {partialBundleOpportunities.map((opportunity) => {
                      const bundleRec = aiRecommendations?.bundleRecommendations?.find(
                        (br) => br.bundleId === opportunity.bundle._id,
                      );
                      const bundleLabel = opportunity.bundle.name.toLowerCase().endsWith('bundle')
                        ? opportunity.bundle.name
                        : `${opportunity.bundle.name} bundle`;
                      return (
                        <Tooltip
                          key={opportunity.bundle._id}
                          content={
                            bundleRec?.reason ? (
                              <div className="max-w-xs p-2">
                                <p className="text-xs">{bundleRec.reason}</p>
                              </div>
                            ) : null
                          }
                          isDisabled={!bundleRec?.reason}
                          delay={300}
                          closeDelay={100}
                          placement="top"
                          color="foreground"
                          showArrow
                        >
                          <button
                            onClick={() =>
                              handleCompleteBundl(
                                opportunity.bundle._id,
                                opportunity.missingServiceIds,
                              )
                            }
                            className="group flex cursor-pointer items-center gap-2 rounded-full border-2 border-warning-300 bg-warning-50 px-4 py-2 text-sm font-medium text-warning-700 transition-all hover:border-warning-500 hover:bg-warning-100"
                          >
                            {bundleRec && (
                              <ConfidenceChip
                                confidence={bundleRec.confidence}
                                showTooltip={false}
                              />
                            )}
                            <span>
                              Add {opportunity.missingCount} service
                              {opportunity.missingCount > 1 ? 's' : ''} for{' '}
                              <strong>{bundleLabel}</strong>
                            </span>
                          </button>
                        </Tooltip>
                      );
                    })}

                    {/* AI Recommended Services */}
                    {unaddedRecommendations.map((rec) => (
                      <Tooltip
                        key={rec.serviceId}
                        content={
                          <div className="max-w-xs p-2">
                            <p className="text-xs">{rec.reason}</p>
                          </div>
                        }
                        delay={300}
                        closeDelay={100}
                        placement="top"
                        color="foreground"
                        showArrow
                      >
                        <button
                          onClick={() => handleAddRecommendedService(rec.serviceId)}
                          className="group flex cursor-pointer items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-medium transition-all"
                          style={{
                            borderColor: `${companyStyles.companyColor || '#006FEE'}40`,
                            backgroundColor: `${companyStyles.companyColor || '#006FEE'}08`,
                            color: companyStyles.companyColor || '#006FEE',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor =
                              companyStyles.companyColor || '#006FEE';
                            e.currentTarget.style.backgroundColor = `${companyStyles.companyColor || '#006FEE'}15`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = `${companyStyles.companyColor || '#006FEE'}40`;
                            e.currentTarget.style.backgroundColor = `${companyStyles.companyColor || '#006FEE'}08`;
                          }}
                        >
                          <ConfidenceChip confidence={rec.confidence} showTooltip={false} />
                          <span>+ {rec.serviceName}</span>
                        </button>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              )}

              {/* Discount Code Input */}
              <div className="mt-4 pt-6">
                <DiscountCodeInput
                  discountCodes={discountCodes}
                  appliedDiscountCode={appliedDiscountCode}
                  appliedDiscountServiceIds={appliedDiscountServiceIds}
                  selectedServiceIds={selectedServiceIds}
                  onApply={(code, serviceIds) => {
                    setAppliedDiscountCode(code);
                    setAppliedDiscountServiceIds(serviceIds);
                  }}
                  onClear={() => {
                    setAppliedDiscountCode(null);
                    setAppliedDiscountServiceIds([]);
                  }}
                />
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">No services selected</p>
          )}
        </div>

        {/* Property Section - Right Side Stacked */}
        <div className="space-y-6">
          {/* Property */}
          <div className="rounded-lg border border-gray-300 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: companyStyles.companyColor
                    ? `${companyStyles.companyColor}1A`
                    : 'rgba(0, 111, 238, 0.1)',
                  color: companyStyles.companyColor || '#006FEE',
                }}
              >
                <FaHouseChimney className="text-lg" />
              </div>
              <h3 className="text-lg font-semibold">Property</h3>
            </div>

            {address ? (
              <div className="space-y-3">
                {streetViewUrl && (
                  <img
                    src={streetViewUrl}
                    alt="Property"
                    className="h-32 w-full rounded-lg object-cover"
                  />
                )}
                <div>
                  <p className="text-sm font-semibold">
                    {`${address.street}, ${
                      address.unit ? `${address.unit}` : ''
                    } ${address.city} ${address.zip}`}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-600">
                    {address.sqft && <span>{address.sqft.toLocaleString()} sqft</span>}
                    {propertyAge && <span>• {propertyAge} years old</span>}
                    {propertyDetails?.bedrooms && (
                      <span>
                        {propertyDetails.bedrooms} bed
                        {propertyDetails.bedrooms !== 1 ? 's' : ''}
                      </span>
                    )}
                    {propertyDetails?.bathrooms && (
                      <span>
                        • {propertyDetails.bathrooms} bath
                        {propertyDetails.bathrooms !== 1 ? 's' : ''}
                      </span>
                    )}
                    {propertyDetails?.foundationType && (
                      <span>• {propertyDetails.foundationType}</span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No property selected</p>
            )}
          </div>

          {/* Slot Section */}
          <div className="rounded-lg border border-gray-300 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-lg"
                  style={{
                    backgroundColor: companyStyles.companyColor
                      ? `${companyStyles.companyColor}1A`
                      : 'rgba(0, 111, 238, 0.1)',
                    color: companyStyles.companyColor || '#006FEE',
                  }}
                >
                  <FaCalendarDay className="text-lg" />
                </div>
                <h3 className="text-lg font-semibold">Appointment</h3>
              </div>
              {scheduleSettings.showInspectorInOnlineScheduler && selectedSlotInspectorInfo && (
                <div className="flex items-center gap-2">
                  <Avatar
                    src={selectedSlotInspectorInfo.thumb || undefined}
                    size="sm"
                    fallback={`${
                      selectedSlotInspectorInfo.firstName?.charAt(0) || ''
                    }${selectedSlotInspectorInfo.lastName?.charAt(0) || ''}`}
                  />
                  <div>
                    <p className="text-sm font-medium">
                      {selectedSlotInspectorInfo.firstName} {selectedSlotInspectorInfo.lastName}
                    </p>
                    <p className="text-xs text-gray-600">Inspector</p>
                  </div>
                </div>
              )}
            </div>

            {selectedDateTime ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center">
                <div
                  className="mb-1 text-2xl font-bold"
                  style={{
                    color: companyStyles.companyColor || '#006FEE',
                  }}
                >
                  {tzDayjs(selectedDateTime).format('MMM D')}
                </div>
                <div className="text-sm font-semibold text-gray-700">
                  {tzDayjs(selectedDateTime).format('dddd')}
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  {tzDayjs(selectedDateTime).format('h:mma')}
                </div>
                {scheduleSettings.showDurationInOnlineScheduler && durationRange && (
                  <div className="mt-1 text-xs text-gray-500">{durationRange} duration</div>
                )}
                {!countdown.isExpired && (
                  <div className="mt-2 text-xs text-warning-700">
                    Hold expires in {countdown.minutes}:
                    {countdown.seconds.toString().padStart(2, '0')}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No slot selected</p>
            )}
          </div>

          {/* Contacts Section */}
          <div className="rounded-lg border border-gray-300 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: companyStyles.companyColor
                    ? `${companyStyles.companyColor}1A`
                    : 'rgba(0, 111, 238, 0.1)',
                  color: companyStyles.companyColor || '#006FEE',
                }}
              >
                <FaUser className="text-lg" />
              </div>
              <h3 className="text-lg font-semibold">Contacts</h3>
            </div>

            <div className="space-y-3">
              {/* Primary Contact */}
              {contact ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="mb-2 flex items-center gap-2">
                    {selectedRole?.icon && (
                      <div
                        style={{
                          color: companyStyles.companyColor || '#006FEE',
                        }}
                      >
                        {getIconComponent(selectedRole.icon)}
                      </div>
                    )}
                    <span className="text-sm font-semibold text-gray-700">
                      {selectedRole?.name || 'Primary Contact'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Avatar
                      src={contact.thumb}
                      size="sm"
                      fallback={`${contact.firstName?.charAt(0)}${contact.lastName?.charAt(0)}`}
                    />
                    <div>
                      <p className="text-sm font-medium">
                        {contact.firstName} {contact.lastName}
                      </p>
                      <p className="text-xs text-gray-600">{contact.email}</p>
                      {contact.phone && (
                        <p className="text-xs text-gray-600">
                          {formatPhoneForDisplay(contact.phone)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500">No primary contact added</p>
              )}

              {/* Additional Contacts */}
              {additionalContacts.length > 0 && (
                <div className="space-y-2">
                  {additionalContacts.map((additionalContact, idx) => {
                    const role = contactRoles.find((r) => r._id === additionalContact._roleId);
                    const contactData = additionalContact._contactId;

                    return (
                      <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-2 flex items-center gap-2">
                          {role?.icon && (
                            <div
                              style={{
                                color: companyStyles.companyColor || '#006FEE',
                              }}
                            >
                              {getIconComponent(role.icon)}
                            </div>
                          )}
                          <span className="text-sm font-semibold text-gray-700">
                            {role?.name || 'Additional Contact'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Avatar
                            src={contactData.thumb}
                            size="sm"
                            fallback={`${contactData.firstName?.charAt(
                              0,
                            )}${contactData.lastName?.charAt(0)}`}
                          />
                          <div>
                            <p className="text-sm font-medium">
                              {contactData.firstName} {contactData.lastName}
                            </p>
                            <p className="text-xs text-gray-600">{contactData.email}</p>
                            {contactData.phone && (
                              <p className="text-xs text-gray-600">
                                {formatPhoneForDisplay(contactData.phone)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notes for the Office */}
      <div className="rounded-lg border border-gray-300 bg-white p-6">
        <div className="mb-4 flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{
              backgroundColor: companyStyles.companyColor
                ? `${companyStyles.companyColor}1A`
                : 'rgba(0, 111, 238, 0.1)',
              color: companyStyles.companyColor || '#006FEE',
            }}
          >
            <FaRegNoteSticky className="text-xl" />
          </div>
          <div>
            <h3 className="text-xl font-semibold">Notes for the Office</h3>
            <p className="text-sm text-gray-500">
              Optional — leave any special instructions or details for the inspection team.
            </p>
          </div>
        </div>
        <textarea
          value={schedulerNotes}
          onChange={(e) => setSchedulerNotes(e.target.value)}
          maxLength={2000}
          rows={4}
          placeholder="e.g. Gate code is 1234. Please call 30 minutes before arriving. Dog will be secured."
          className="w-full resize-none rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none transition-colors focus:border-gray-400 focus:bg-white"
          style={{
            '--tw-ring-color': companyStyles.companyColor || '#006FEE',
          } as React.CSSProperties}
        />
        <p className="mt-1 text-right text-xs text-gray-400">
          {schedulerNotes.length} / 2000
        </p>
      </div>
    </div>
  );
}
